/**
 * Validation, tiered.
 *
 * Three tiers, because they answer three different questions at three different moments:
 *
 *   `validateBlock`  — cheap, per-edit. Do this block's props match its declared fields?
 *   `validateSite`   — whole-document, at publish. Plus the invariants no single block can see:
 *                      unique ids, resolvable links, unique slugs, a handle that can be a hostname.
 *   `readyToPublish` — the *warnings*: empty required fields and half-translated copy. These never
 *                      block editing and they do not block publishing either — they are shown, and
 *                      the customer decides. A half-built site is the normal state of a site being
 *                      built.
 *
 * ⚠️ SEVERITY IS PART OF AN ISSUE, WHICH THE PREDECESSOR'S VERSION LACKED. There, every finding was
 * an error, so "you have not written the Arabic hero yet" and "this link points at a deleted page"
 * arrived as the same red. The consequence is predictable and it is the whole reason the
 * distinction exists: a screen where everything is an error is a screen where nothing is.
 *
 * A small dependency-free walker rather than Zod, on purpose. It is not a size argument — it is that
 * the *schema is data* here (`FieldSpec`), so the validator is a fold over that data. Generating Zod
 * from `FieldSpec` and then running it would be the same walk with a build step in the middle.
 */

import { getBlockDef } from './registry/index.ts'
import { BLOCK_WIDTHS } from './layout.ts'
import { GROUND_ROLES, type FieldSpec } from './prop-types.ts'
import { isId } from './id.ts'
import { isPartial, isText, hasText, type Text } from './text.ts'
import type { Block, Link, Page, Site } from './types.ts'

export type Severity = 'error' | 'warning'

export type Issue = {
  /** A dotted/bracketed path into the document, for pointing the editor at the problem. */
  path: string
  severity: Severity
  /**
   * ⚠️ BOTH LANGUAGES, ALWAYS — deliberately `{ en, ar }` rather than the document's `Text`, which
   * also admits `{ lit }`. A refusal a Saudi customer cannot read is not a refusal, and `lit` is the
   * escape hatch that would let one ship: it exists for identifiers a customer typed (a phone
   * number, a brand name), and a validation message is neither.
   */
  message: { en: string; ar: string }
  /** Where the editor should navigate to fix it. Absent for site-level findings. */
  where?: { pageId?: string; blockId?: string }
}

const err = (path: string, en: string, ar: string, where?: Issue['where']): Issue => ({
  path,
  severity: 'error',
  message: { en, ar },
  ...(where ? { where } : {}),
})

const warn = (path: string, en: string, ar: string, where?: Issue['where']): Issue => ({
  path,
  severity: 'warning',
  message: { en, ar },
  ...(where ? { where } : {}),
})

export function errorsOnly(issues: Issue[]): Issue[] {
  return issues.filter((i) => i.severity === 'error')
}

// --- the small rules, exported because more than one layer must apply the same one -------------

/** English slug: lowercase letters, digits and single hyphens. Arabic slugs are banned by design —
 *  see `types.ts`. */
export function isSlug(value: string): boolean {
  return /^[a-z0-9]+(?:-[a-z0-9]+)*$/.test(value)
}

/**
 * A handle is a DNS label, which is a stricter thing than a slug even though the character set looks
 * the same.
 *
 * ⚠️ THE 63-CHARACTER CAP IS NOT DECORATION. `<handle>.ksa-web.app` is a real hostname and a DNS
 * label above 63 octets is not resolvable — a site accepted at creation that no browser can ever
 * reach, with the failure appearing at publish rather than at the field where it was typed.
 */
export function isHandle(value: string): boolean {
  return value.length >= 3 && value.length <= 63 && isSlug(value)
}

/**
 * Handles that must never be issued.
 *
 * ⚠️ THIS IS A SECURITY LIST, NOT A TIDINESS LIST. `www`, `api` and `mail` are infrastructure names;
 * the rest are the ones that let a customer's site *impersonate the vendor* — a page at
 * `billing.ksa-web.app` asking for a card is indistinguishable from us to anybody who trusts the
 * domain. The cost of reserving a word is that one customer picks a different name.
 */
export const RESERVED_HANDLES = new Set([
  'www', 'api', 'app', 'admin', 'mail', 'smtp', 'ftp', 'ns', 'ns1', 'ns2', 'mx', 'cdn', 'static',
  'assets', 'media', 'img', 'images', 'files', 'download', 'downloads',
  'auth', 'login', 'signin', 'signup', 'account', 'accounts', 'billing', 'pay', 'payment',
  'payments', 'checkout', 'invoice', 'invoices', 'secure', 'security', 'verify', 'verification',
  'support', 'help', 'status', 'docs', 'blog', 'news', 'about', 'legal', 'privacy', 'terms',
  'ksa', 'ksaweb', 'ksa-web', 'aliweb', 'ali-web-services', 'analytics', 'test', 'staging', 'dev',
  'demo', 'preview', 'edge', 'worker', 'internal', 'root', 'system',
])

/**
 * An external href must be http(s) and parseable.
 *
 * ⚠️ EXPORTED SO THE RENDERER ENFORCES THE SAME RULE, not because two callers happened to want it. A
 * document can reach the renderer unvalidated — a draft restored from localStorage is a raw
 * `JSON.parse`, and an AI patch is another such path — so the scheme check must exist at the point
 * an `href` is emitted, not only at the point a document is checked. **And it matters more here than
 * it did in the predecessor**: publishing emits static HTML outside React, which carries none of
 * react-dom's `javascript:` protection. One rule, one place, both callers.
 */
export function isHttpUrl(value: string): boolean {
  try {
    const u = new URL(value)
    return u.protocol === 'https:' || u.protocol === 'http:'
  } catch {
    return false
  }
}

/**
 * An image key is an R2 object key, never bytes.
 *
 * ⚠️ THE `data:` CHECK IS THE POINT OF THIS FUNCTION. A 1 MiB Firestore document is roughly four
 * pasted data URIs, and a customer whose upload is slow will paste one the first chance they get.
 * Rejecting it *here* means the failure lands on the field, in the editor, with a sentence — rather
 * than as a 413 on save, after twenty minutes of work.
 */
export function isImageKey(value: unknown): value is string {
  return (
    typeof value === 'string' &&
    value.length > 0 &&
    value.length <= 300 &&
    !value.includes('..') &&
    /^[a-zA-Z0-9/_.-]+$/.test(value)
  )
}

// --- the walker ---------------------------------------------------------------------------------

function validateValue(field: FieldSpec, value: unknown, path: string, where?: Issue['where']): Issue[] {
  const t = field.type
  const bad = (en: string, ar: string): Issue[] => [err(path, en, ar, where)]

  switch (t.kind) {
    case 'text':
    case 'longText':
      return isText(value) ? [] : bad('Expected a bilingual text value.', 'قيمة نصية ثنائية اللغة متوقعة.')

    case 'image': {
      if (!value || typeof value !== 'object') return bad('Expected an image.', 'صورة متوقعة.')
      const o = value as Record<string, unknown>
      if (o.status === 'placeholder') {
        return isText(o.description) ? [] : bad('A placeholder needs a description.', 'العنصر النائب يحتاج وصفًا.')
      }
      if (o.status === 'ready') {
        if (!isImageKey(o.key)) {
          return bad(
            'That image is not stored properly — upload it again.',
            'لم تُخزَّن الصورة بشكل صحيح — أعد رفعها.',
          )
        }
        return isText(o.alt) ? [] : bad('An image needs alternative text.', 'الصورة تحتاج نصًا بديلًا.')
      }
      return bad('Unknown image state.', 'حالة صورة غير معروفة.')
    }

    case 'link':
      return validateLink(value as Link, path, where)

    // ⚠️ GROUND_ROLES, not SITE_ROLES — see the long note in prop-types.ts. This NARROWS what
    // validates, so a document carrying a value from before the grounds were narrowed reports an
    // error rather than rendering in a colour nobody chose. That is the point: the alternative is a
    // site that publishes with invisible text and no warning.
    case 'colorToken':
      return (GROUND_ROLES as readonly string[]).includes(value as string)
        ? []
        : bad('Not a usable background colour.', 'ليس لونًا صالحًا للخلفية.')

    case 'enum':
      return t.options.some((o) => o.value === value)
        ? []
        : bad('That choice is not one of the options.', 'هذا الخيار ليس ضمن الخيارات المتاحة.')

    case 'number': {
      if (typeof value !== 'number' || !Number.isFinite(value)) return bad('Expected a number.', 'رقم متوقع.')
      if (t.min !== undefined && value < t.min) return bad(`Must be at least ${t.min}.`, `يجب ألا يقل عن ${t.min}.`)
      if (t.max !== undefined && value > t.max) return bad(`Must be at most ${t.max}.`, `يجب ألا يزيد عن ${t.max}.`)
      return []
    }

    case 'boolean':
      return typeof value === 'boolean' ? [] : bad('Expected true or false.', 'قيمة منطقية متوقعة.')

    case 'list': {
      if (!Array.isArray(value)) return bad('Expected a list.', 'قائمة متوقعة.')
      const issues: Issue[] = []
      // ⚠️ THE BOUNDS ARE AN ERROR, NOT A WARNING, because the caller that overruns them is not a
      // person. A customer cannot add a 25th gallery item — the editor's Add button is disabled. A
      // model asked for "a few features" will emit forty, and the block that renders forty is
      // unusable on a phone. This is where that gets caught.
      if (t.max !== undefined && value.length > t.max) {
        issues.push(err(path, `At most ${t.max} allowed here.`, `الحد الأقصى ${t.max} هنا.`, where))
      }
      if (t.min !== undefined && value.length > 0 && value.length < t.min) {
        issues.push(err(path, `At least ${t.min} needed here.`, `مطلوب ${t.min} على الأقل هنا.`, where))
      }
      value.forEach((item, i) => {
        const row = (item ?? {}) as Record<string, unknown>
        for (const sub of t.item) {
          issues.push(...validateValue(sub, row[sub.key], `${path}[${i}].${sub.key}`, where))
        }
      })
      return issues
    }
  }
}

function validateLink(link: Link, path: string, where?: Issue['where']): Issue[] {
  if (!link || typeof link !== 'object') return [err(path, 'Expected a link.', 'رابط متوقع.', where)]
  if (link.kind === 'none') return []
  if (link.kind === 'page') {
    return typeof link.pageId === 'string' && link.pageId.length > 0
      ? []
      : [err(path, 'This link has no destination.', 'هذا الرابط بلا وجهة.', where)]
  }
  if (link.kind === 'external') {
    return isHttpUrl(link.href)
      ? []
      : [err(path, 'An external link must start with http:// or https://.', 'الرابط الخارجي يجب أن يبدأ بـ ‎http://‎ أو ‎https://‎.', where)]
  }
  return [err(path, 'Unknown kind of link.', 'نوع رابط غير معروف.', where)]
}

/** The cheap per-edit pass: one block's props against its declared fields. */
export function validateBlock(block: Block, path = `block:${block.id}`, where?: Issue['where']): Issue[] {
  const def = getBlockDef(block.type)
  if (!def) {
    return [err(path, `Unknown block type "${block.type}".`, `نوع كتلة غير معروف "${block.type}".`, where)]
  }
  const issues: Issue[] = []
  if (block.variant !== undefined && !def.variants.some((v) => v.value === block.variant)) {
    issues.push(err(`${path}.variant`, `Unknown layout "${block.variant}".`, `تخطيط غير معروف "${block.variant}".`, where))
  }
  if (block.width !== undefined && !BLOCK_WIDTHS.includes(block.width)) {
    issues.push(err(`${path}.width`, `Unknown width "${block.width}".`, `عرض غير معروف "${block.width}".`, where))
  }
  for (const f of def.fields) {
    issues.push(...validateValue(f, block.props[f.key], `${path}.${f.key}`, where))
  }
  return issues
}

/**
 * The whole-document pass. Every block, plus the invariants no single block can see.
 *
 * ⚠️ THIS RUNS ON THE SERVER AT PUBLISH, from this same package (CLAUDE.md §2.9). The API treats a
 * *saved* document as opaque content — nothing inside it grants anybody anything — but a *published*
 * one is a public artefact served under our certificate, so the check that a link is `http(s)` and a
 * slug is a slug cannot be left to a browser that a customer controls.
 */
export function validateSite(site: Site): Issue[] {
  const issues: Issue[] = []

  if (!isHandle(site.handle)) {
    issues.push(
      err(
        'site.handle',
        'The address must be 3–63 characters: lowercase letters, digits and hyphens.',
        'العنوان يجب أن يكون من ٣ إلى ٦٣ حرفًا: أحرف إنجليزية صغيرة وأرقام وشرطات.',
      ),
    )
  } else if (RESERVED_HANDLES.has(site.handle)) {
    issues.push(err('site.handle', 'That address is reserved.', 'هذا العنوان محجوز.'))
  }

  if (site.pages.length === 0) {
    issues.push(err('site.pages', 'A site needs at least one page.', 'الموقع يحتاج صفحة واحدة على الأقل.'))
  }
  if (site.defaultLang !== 'en' && site.defaultLang !== 'ar') {
    issues.push(err('site.defaultLang', 'The main language must be English or Arabic.', 'اللغة الرئيسية يجب أن تكون الإنجليزية أو العربية.'))
  }

  issues.push(...validateBlock(site.navbar, 'site.navbar', { blockId: site.navbar.id }))
  issues.push(...validateBlock(site.footer, 'site.footer', { blockId: site.footer.id }))

  const pageIds = new Set(site.pages.map((p) => p.id))
  const slugs = new Set<string>()
  // ⚠️ IDS ARE CHECKED ACROSS THE WHOLE DOCUMENT, not per page, because an anchor link and the
  // editor's own block reference both address a block by id alone. Two blocks sharing one id makes
  // "edit this block" ambiguous, and the way that presents is edits landing on the wrong block —
  // which reads as data loss, not as a duplicate id.
  const seenIds = new Set<string>()
  const dupe = (id: string, path: string) => {
    if (seenIds.has(id)) issues.push(err(path, 'Two things in this site share one id.', 'عنصران في هذا الموقع يحملان المعرّف نفسه.'))
    seenIds.add(id)
  }
  dupe(site.navbar.id, 'site.navbar.id')
  dupe(site.footer.id, 'site.footer.id')

  for (const page of site.pages) {
    const base = `page:${page.id}`
    const where = { pageId: page.id }
    dupe(page.id, `${base}.id`)
    if (!isId(page.id)) issues.push(err(`${base}.id`, 'A page has a malformed id.', 'معرّف صفحة غير صالح.', where))

    if (!isSlug(page.slug)) {
      issues.push(
        err(
          `${base}.slug`,
          `The address "${page.slug}" must be English: lowercase letters, digits and hyphens.`,
          `المسار "${page.slug}" يجب أن يكون إنجليزيًا: أحرف صغيرة وأرقام وشرطات.`,
          where,
        ),
      )
    }
    if (slugs.has(page.slug)) {
      issues.push(err(`${base}.slug`, `Two pages both use the address "${page.slug}".`, `صفحتان تستخدمان المسار "${page.slug}".`, where))
    }
    slugs.add(page.slug)

    const blockIds = new Set(page.blocks.map((b) => b.id))
    for (const block of page.blocks) {
      const spot = { pageId: page.id, blockId: block.id }
      dupe(block.id, `${base}/block:${block.id}.id`)
      issues.push(...validateBlock(block, `${base}/block:${block.id}`, spot))
      issues.push(...unresolvedLinks(block, pageIds, blockIds, `${base}/block:${block.id}`, spot))
      // A site-scoped block on a page (or the reverse) is a document a patch produced, never a
      // customer. It renders, which is why it needs saying: two navbars is a page with two navbars.
      const def = getBlockDef(block.type)
      if (def && def.scope !== 'page') {
        issues.push(err(`${base}/block:${block.id}`, `"${block.type}" cannot be placed on a page.`, `لا يمكن وضع "${block.type}" داخل صفحة.`, spot))
      }
    }
  }

  issues.push(...unresolvedLinks(site.navbar, pageIds, new Set(), 'site.navbar', { blockId: site.navbar.id }))
  issues.push(...unresolvedLinks(site.footer, pageIds, new Set(), 'site.footer', { blockId: site.footer.id }))

  return issues
}

/** Every internal link must resolve to a page that exists, and an anchor to a block on that same
 *  page. The invariant a single block cannot check for itself — which is exactly why deleting a page
 *  is not allowed to be a silent operation. */
function unresolvedLinks(
  block: Block,
  pageIds: Set<string>,
  samePageBlockIds: Set<string>,
  base: string,
  where?: Issue['where'],
): Issue[] {
  const issues: Issue[] = []
  const walk = (value: unknown, path: string) => {
    if (Array.isArray(value)) {
      value.forEach((v, i) => walk(v, `${path}[${i}]`))
      return
    }
    if (value && typeof value === 'object') {
      const o = value as Record<string, unknown>
      if (o.kind === 'page' && typeof o.pageId === 'string') {
        if (!pageIds.has(o.pageId)) {
          issues.push(err(path, 'This link points at a page that no longer exists.', 'هذا الرابط يشير إلى صفحة لم تعد موجودة.', where))
        }
        if (typeof o.anchor === 'string' && o.anchor && samePageBlockIds.size > 0 && !samePageBlockIds.has(o.anchor)) {
          issues.push(err(path, 'This link points at a section that no longer exists.', 'هذا الرابط يشير إلى قسم لم يعد موجودًا.', where))
        }
        return
      }
      for (const [k, v] of Object.entries(o)) walk(v, `${path}.${k}`)
    }
  }
  walk(block.props, base)
  return issues
}

// --- the warnings tier ---------------------------------------------------------------------------

/**
 * What is *unfinished*, as distinct from what is *wrong*.
 *
 * These never block anything. They are the publish screen's checklist, and the reason they are
 * computed here rather than in the editor is that the publish confirmation and the editor's own
 * per-block hints must agree — two implementations of "is this field empty" is two answers on the
 * same screen.
 */
export function readyToPublish(site: Site): Issue[] {
  const issues: Issue[] = []

  const checkBlock = (block: Block, base: string, where: Issue['where']) => {
    const def = getBlockDef(block.type)
    if (!def) return
    for (const f of def.fields) {
      const value = block.props[f.key]
      if (f.requiredForPublish && isEmptyValue(value)) {
        issues.push(
          warn(
            `${base}.${f.key}`,
            `"${textIn(f.label, 'en')}" is empty.`,
            `"${textIn(f.label, 'ar')}" فارغ.`,
            where,
          ),
        )
      }
      issues.push(...untranslated(value, `${base}.${f.key}`, where))
    }
  }

  checkBlock(site.navbar, 'site.navbar', { blockId: site.navbar.id })
  checkBlock(site.footer, 'site.footer', { blockId: site.footer.id })
  for (const page of site.pages) {
    if (isPartial(page.title)) {
      issues.push(warn(`page:${page.id}.title`, 'This page’s name is missing a language.', 'اسم هذه الصفحة ناقص بإحدى اللغتين.', { pageId: page.id }))
    }
    if (page.blocks.length === 0) {
      issues.push(warn(`page:${page.id}`, 'This page is empty.', 'هذه الصفحة فارغة.', { pageId: page.id }))
    }
    for (const block of page.blocks) {
      checkBlock(block, `page:${page.id}/block:${block.id}`, { pageId: page.id, blockId: block.id })
    }
  }

  // ⚠️ A PLACEHOLDER IMAGE IS A WARNING AND NOT AN ERROR, deliberately. The dashed box with the
  // description in it IS a legitimate published state — plenty of small sites go live before the
  // photographs exist, and refusing to publish over it would mean the product's own
  // "describe it now, generate later" flow could not reach a live site.
  const placeholders = countPlaceholders(site)
  if (placeholders > 0) {
    issues.push(
      warn(
        'site.images',
        `${placeholders} image${placeholders === 1 ? '' : 's'} ${placeholders === 1 ? 'is' : 'are'} still a description rather than a picture.`,
        `${placeholders} صورة ما زالت وصفًا وليست صورة فعلية.`,
      ),
    )
  }
  return issues
}

function countPlaceholders(site: Site): number {
  let n = 0
  const walk = (value: unknown) => {
    if (Array.isArray(value)) return value.forEach(walk)
    if (value && typeof value === 'object') {
      const o = value as Record<string, unknown>
      if (o.status === 'placeholder') n++
      else Object.values(o).forEach(walk)
    }
  }
  for (const b of [site.navbar, site.footer, ...site.pages.flatMap((p) => p.blocks)]) walk(b.props)
  return n
}

function untranslated(value: unknown, path: string, where: Issue['where']): Issue[] {
  const out: Issue[] = []
  const walk = (v: unknown, p: string) => {
    if (Array.isArray(v)) return v.forEach((x, i) => walk(x, `${p}[${i}]`))
    if (v && typeof v === 'object') {
      if (isText(v)) {
        if (isPartial(v)) {
          const missing = 'en' in v && !v.en ? 'English' : 'Arabic'
          const missingAr = missing === 'English' ? 'الإنجليزية' : 'العربية'
          out.push(warn(p, `Missing the ${missing} version.`, `النسخة ${missingAr} ناقصة.`, where))
        }
        return
      }
      for (const [k, x] of Object.entries(v as Record<string, unknown>)) walk(x, `${p}.${k}`)
    }
  }
  walk(value, path)
  return out
}

function isEmptyValue(value: unknown): boolean {
  if (value === undefined || value === null) return true
  if (Array.isArray(value)) return value.length === 0
  if (isText(value)) return !hasText(value)
  if (typeof value === 'object') {
    const o = value as Record<string, unknown>
    if (o.status === 'placeholder') return !hasText(o.description as Text)
    if (o.kind === 'none') return true
  }
  return false
}

function textIn(t: Text, lang: 'en' | 'ar'): string {
  return 'lit' in t ? t.lit : t[lang]
}
